import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db, firebaseReady, firebaseConfigError } from '../firebase/config';
import { apiClient } from '../api/client';
import toast from 'react-hot-toast';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    console.error('❌ useAuth: Context is undefined - AuthProvider not found in tree');
    throw new Error('useAuth must be used within an AuthProvider');
  }
  if (typeof context.login !== 'function') {
    console.error('❌ useAuth: login function is missing from context', context);
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Remove cross-tab sync - each tab maintains its own auth state

  useEffect(() => {
    console.log('🔥 AuthContext: Setting up auth state listener');

    if (!firebaseReady || !auth) {
      console.error('❌ AuthContext: Firebase is not ready:', firebaseConfigError || 'Unknown Firebase config error');
      setLoading(false);
      return;
    }
    
    // Try to restore from sessionStorage first
    const savedUser = sessionStorage.getItem('userData');
    const savedToken = sessionStorage.getItem('authToken');
    if (savedUser && savedToken) {
      try {
        const parsedUser = JSON.parse(savedUser);
        console.log('💾 AuthContext: Restored user from session:', parsedUser.email);
        setUser(parsedUser);
        setToken(savedToken);
      } catch (e) {
        console.error('❌ Failed to restore session:', e);
        sessionStorage.removeItem('userData');
        sessionStorage.removeItem('authToken');
      }
    }
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🔥 AuthContext: Auth state changed', { 
        hasUser: !!firebaseUser, 
        email: firebaseUser?.email,
        uid: firebaseUser?.uid,
        hasSessionData: !!(savedUser && savedToken)
      });
      
      try {
        if (firebaseUser) {
          console.log('🔑 AuthContext: Getting ID token for user:', firebaseUser.email);
          // Force refresh to get latest custom claims
          const idToken = await firebaseUser.getIdToken(true);
          const tokenResult = await firebaseUser.getIdTokenResult(true);
          console.log('✅ AuthContext: ID token obtained (forced refresh)');
          console.log('🔍 AuthContext: Token claims:', tokenResult.claims);
          
          setToken(idToken);
          sessionStorage.setItem('authToken', idToken);
          
          const role = tokenResult.claims.role || 'user';
          console.log('👤 AuthContext: Final role assigned:', role);
          
          const userData = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName || firebaseUser.email.split('@')[0],
            role: role,
            customer_id: tokenResult.claims.customer_id || null,
            isB2C: !tokenResult.claims.customer_id
          };
          
          console.log('👤 AuthContext: Setting user data:', { 
            email: userData.email, 
            role: userData.role, 
            uid: userData.uid 
          });
          
          setUser(userData);
          sessionStorage.setItem('userData', JSON.stringify(userData));
          console.log('✅ AuthContext: User authenticated with role:', role);
        } else {
          const hasSessionData = sessionStorage.getItem('userData') && sessionStorage.getItem('authToken');
          if (!hasSessionData) {
            console.log('🚪 AuthContext: User signed out, clearing data');
            setUser(null);
            setToken(null);
            sessionStorage.removeItem('authToken');
            sessionStorage.removeItem('userData');
          } else {
            console.log('⏸️ AuthContext: No Firebase user but session exists, keeping session');
          }
        }
      } catch (error) {
        console.error('❌ AuthContext: Auth state change error:', error);
        setUser(null);
        setToken(null);
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('userData');
      } finally {
        console.log('✅ AuthContext: Auth loading complete');
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    console.log('🔑 AuthContext: Login attempt for:', email);
    try {
      if (!auth) {
        throw new Error(firebaseConfigError || 'Firebase auth is not configured');
      }
      console.log('🔄 AuthContext: Calling Firebase signIn...');
      const result = await signInWithEmailAndPassword(auth, email, password);
      console.log('✅ AuthContext: Login successful for:', email);

      const params = new URLSearchParams(window.location.search);
      const redirect = params.get('redirect');
      const ref = params.get('ref');

      if (redirect) {
        if (ref) {
          sessionStorage.setItem('assessment_ref', ref);
        } else {
          sessionStorage.removeItem('assessment_ref');
        }

        const safeRedirect = redirect.startsWith('/') ? redirect : '/assessment';
        window.location.assign(safeRedirect);
      }

      toast.success('Signed in successfully');
      return result;
    } catch (error) {
      console.error('❌ AuthContext: Login error:', error.code, error.message);
      toast.error(getAuthErrorMessage(error.code));
      throw error;
    }
  };

  const register = async (email, password, displayName) => {
    try {
      setLoading(true);
      if (!auth || !db) {
        throw new Error(firebaseConfigError || 'Firebase is not configured');
      }
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update profile
      await updateProfile(result.user, { displayName });
      
      // Create user document in Firestore
      await setDoc(doc(db, 'users', result.user.uid), {
        email,
        displayName,
        role: 'user',
        createdAt: new Date(),
        profileComplete: false,
        testHistory: []
      });

      // Register as B2C user in backend
      try {
        await apiClient.post('/auth/public-signup', {
          email,
          displayName,
          firebaseUid: result.user.uid
        });
      } catch (backendError) {
        console.warn('Backend B2C registration failed:', backendError);
      }

      toast.success('Account created successfully');
      return result;
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(getAuthErrorMessage(error.code));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    console.log('🚪 AuthContext: Logout initiated');
    try {
      if (!auth) {
        setUser(null);
        setToken(null);
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('userData');
        return;
      }
      await signOut(auth);
      setUser(null);
      setToken(null);
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('userData');
      console.log('✅ AuthContext: Logout successful');
      toast.success('Signed out successfully');
    } catch (error) {
      console.error('❌ AuthContext: Logout error:', error);
      toast.error('Error signing out');
    }
  };

  const getUserProfile = async () => {
    if (!user) return null;
    
    try {
      const response = await apiClient.get('/auth/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get user profile:', error);
      return null;
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    getUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

const getAuthErrorMessage = (errorCode) => {
  switch (errorCode) {
    case 'auth/user-not-found':
      return 'No account found with this email address';
    case 'auth/wrong-password':
      return 'Incorrect password';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters';
    case 'auth/invalid-email':
      return 'Invalid email address';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later';
    default:
      return 'Authentication failed. Please try again';
  }
};
